import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetUserProfileQuery, useCreatePaymentOrderMutation, useVerifyPaymentMutation } from "../redux/api/roomBookingApi";

const BillingPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  // APIs
  const { data: userProfile } = useGetUserProfileQuery();
  const [createOrder, { isLoading: isOrderLoading }] = useCreatePaymentOrderMutation();
  const [verifyPayment, { isLoading: isVerifyLoading }] = useVerifyPaymentMutation();

  // Form State
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email_id: "",
    aadhar_number: "",
    address: ""
  });


  // Load cart data from localStorage
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cartData')) || [];
    setCartItems(savedCart);
    calculateTotal(savedCart);
  }, []);

  // Auto-fill form when user data is available
  useEffect(() => {
    if (userProfile?.data?.user) {
      const u = userProfile.data.user;
      const bookings = userProfile.data.bookings || [];

      // Find the most recent Aadhar (proof_type === 1) from bookings
      const lastAadharBooking = bookings.find(b => b.proof_type === 1);
      const aadharNum = lastAadharBooking ? lastAadharBooking.id_number : "";

      setForm(prev => ({
        ...prev,
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        email_id: u.email_id || "",
        address: u.address || "",
        phone_num: u.phone_num || "",
        aadhar_number: aadharNum
      }));
    }
  }, [userProfile]);


  const calculateTotal = (items) => {
    const total = items.reduce((sum, item) => sum + Number(item.seva_amount), 0);
    setTotalAmount(total);
  };

  const handleRemoveItem = (indexToRemove) => {
    const updatedCart = cartItems.filter((_, index) => index !== indexToRemove);
    setCartItems(updatedCart);
    localStorage.setItem('cartData', JSON.stringify(updatedCart));
    calculateTotal(updatedCart);
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      alert("Your cart is empty!");
      return;
    }

    try {
      const bookingId = `SEVA-${Date.now()}`;
      // Determine Customer ID
      let customerId = "GUEST";
      if (userProfile?.data?.user?._id) {
        customerId = userProfile.data.user._id;
      } else if (form.email_id) {
        customerId = form.email_id.replace(/[^a-zA-Z0-9]/g, '_');
      }

      const orderPayload = {
        booking_id: bookingId,
        amount: totalAmount,
        customer_id: customerId,
        customer_phone: form.phone_num || "9999999999",
        customer_name: `${form.first_name} ${form.last_name}`,
        customer_email: form.email_id,
        customer_address: form.address,
        order_items: cartItems
      };

      // Create Order
      const orderRes = await createOrder(orderPayload).unwrap();

      if (orderRes.access_key) {
        const env = orderRes.env || 'prod'; // Read env from backend response
        const paymentUrl = `https://${env === 'test' ? 'testpay' : 'pay'}.easebuzz.in/pay/${orderRes.access_key}`;
        window.location.href = paymentUrl;
      } else {
        alert("Failed to initiate payment with Easebuzz");
      }
    } catch (err) {
      console.error(err);
      alert(err?.data?.message || "❌ Payment initialization failed");
    }
  };

  const handleCheckout = () => {
    const token = localStorage.getItem('uid');
    if (!token) {
      // User not logged in, redirect to login
      if (window.confirm("You must be logged in to checkout. Proceed to Login?")) {
        navigate('/login', { state: { from: '/billing' } });
      }
      return;
    }

    // User logged in, open modal
    const modalElement = document.getElementById('exampleModal');
    if (window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement) || new window.bootstrap.Modal(modalElement);
      modal.show();
    } else {
      // Fallback or ensure bootstrap script is loaded
      alert("System Error: Bootstrap modal not found. Please refresh.");
    }
  };

  // Group items by deity (god_name)
  const groupedItems = cartItems.reduce((acc, item) => {
    const godName = item.god_name || 'Other Sevas';
    if (!acc[godName]) acc[godName] = [];
    acc[godName].push(item);
    return acc;
  }, {});

  let serialNumber = 1;

  return (
    <>
      <style>
        {`
        .bill {
          border-radius: 16px;
          box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
          overflow: hidden;
        }
        .bill table {
          overflow: hidden;
          border-radius: 16px;
          background-color: #fff;
          width: 100%;
        }
        .bill th {
          background-color: #840000;
          color: #fff !important;
          padding: 10px;
        }
        .bill th p {
          color: #fff !important;
        }
        .bill td p {
          color: #000 !important;
        }
        .bill td {
          color: #000 !important;
          padding: 10px;
        }
        .bill-table-container {
          background-color: #ffd54f;
        }
        .checkout {
          background-color: #ffd54f;
        }
        .checkout table {
          background-color: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
          width: 100%;
        }
        .checkout .th-btn {
          box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
          background-color: #fff;
          color: #840000;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: bold;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .checkout .th-btn:hover {
          background-color: #840000;
          color: #fff;
        }
        .checkout .th-btn .btn-text {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        `}
      </style>

      <section className="billing-section space">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7">
              <div className="p-4 bill-table-container border rounded">
                <div className="bill table-responsive">
                  <table className="m-0" id="billingTable" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th><p className="text-center m-0">Sl No.</p></th>
                        <th><p className="text-center m-0">Pooja</p></th>
                        <th><p className="text-center m-0">Price</p></th>
                        <th><p className="text-center m-0">Remove</p></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.length > 0 ? (
                        Object.keys(groupedItems).map((godName) => (
                          <React.Fragment key={godName}>
                            <tr>
                              <td colSpan="4">
                                <h1 className="fs-5 m-0 text-center">{godName}</h1>
                              </td>
                            </tr>
                            {groupedItems[godName].map((item) => {
                              // We need to find the original index in cartItems to remove correctly
                              const originalIndex = cartItems.indexOf(item);
                              return (
                                <tr key={originalIndex}>
                                  <td>
                                    <p className="m-0 text-start"><b>{serialNumber++}.</b></p>
                                  </td>
                                  <td>
                                    <p className="m-0 text-center">{item.seva_name}</p>
                                  </td>
                                  <td>
                                    <p className="m-0 text-center">₹{item.seva_amount}</p>
                                  </td>
                                  <td>
                                    <p className="m-0 text-center">
                                      <b>
                                        <button
                                          type="button"
                                          className="th-btn th-icon"
                                          onClick={(e) => { e.preventDefault(); handleRemoveItem(originalIndex); }}
                                          style={{ padding: '8px 12px', border: 'none', background: 'transparent' }}
                                        >
                                          <span className="btn-text">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-trash3" viewBox="0 0 16 16">
                                              <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                            </svg>
                                          </span>
                                        </button>
                                      </b>
                                    </p>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center p-3">
                            <p className="m-0">Your cart is empty.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-4 mt-5 mt-lg-0">
              <div className="checkout border rounded p-4">
                <table className="m-0" style={{ width: "100%" }}>
                  <tbody>
                    <tr>
                      <td className="border-0">
                        <div>
                          <p className="text-start fs-4 m-0" style={{ color: '#000' }}>
                            Total Price
                          </p>
                        </div>
                      </td>
                      <td className="border-0">
                        <div className="text-end">
                          <p className="m-0 fs-4" style={{ color: '#840000' }}>
                            <b>₹ {totalAmount}</b>
                          </p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-3 d-flex justify-content-center">
                  <button className="th-btn th-icon" onClick={handleCheckout} style={{ border: 'none' }}>
                    <span className="btn-text">
                      Check Out
                      <span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                          className="bi bi-box-arrow-right" viewBox="0 0 16 16">
                          <path fillRule="evenodd"
                            d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z" />
                          <path fillRule="evenodd"
                            d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z" />
                        </svg>
                      </span>
                    </span>
                  </button>
                  <div className="modal fade" id="exampleModal" tabIndex="-1" aria-labelledby="exampleModalLabel"
                    aria-hidden="true" style={{ zIndex: 99 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                      <div className="modal-content">
                        <div className="modal-header">
                          <div className="d-flex justify-content-center">
                            <img style={{ width: '90%' }} src="assets/img/logoji.svg" alt="Hariharapura" />
                          </div>
                        </div>
                        <div className="modal-body">
                          <div className="form-container">
                            <form onSubmit={handlePayment}>
                              <div className="row">
                                <div className="col-lg-6">
                                  <div className="form-group">
                                    <input
                                      type="text"
                                      placeholder="First Name"
                                      name="first_name"
                                      value={form.first_name}
                                      onChange={handleInputChange}
                                      required
                                      className="form-control"
                                    />
                                  </div>
                                </div>
                                <div className="col-lg-6">
                                  <div className="form-group">
                                    <input
                                      type="text"
                                      placeholder="Last Name"
                                      name="last_name"
                                      value={form.last_name}
                                      onChange={handleInputChange}
                                      required
                                      className="form-control"
                                    />
                                  </div>
                                </div>
                                <div className="col-lg-6">
                                  <div className="form-group">
                                    <input
                                      type="email"
                                      placeholder="Email"
                                      name="email_id"
                                      value={form.email_id}
                                      onChange={handleInputChange}
                                      required
                                      className="form-control"
                                    />
                                  </div>
                                </div>
                                <div className="col-lg-6">
                                  <div className="form-group">
                                    <input
                                      type="text"
                                      placeholder="Aadhar Number"
                                      name="aadhar_number"
                                      value={form.aadhar_number}
                                      onChange={handleInputChange}
                                      className="form-control"
                                    />
                                  </div>
                                </div>
                                <div className="col-lg-12">
                                  <div className="form-group">
                                    <textarea
                                      name="address"
                                      placeholder="Address"
                                      value={form.address}
                                      onChange={handleInputChange}
                                      className="form-control"
                                    ></textarea>
                                  </div>
                                </div>
                                <div className="col-lg-12 row align-items-center">
                                  <div className="col-lg-6 d-flex justify-content-center">
                                    <button type="submit" className="btn btn-primary" disabled={isOrderLoading || isVerifyLoading}>
                                      {isOrderLoading ? "Processing..." : "Submit Payment"}
                                    </button>
                                  </div>
                                  <div className="col-lg-6 mt-4 mt-lg-0 d-flex justify-content-center">
                                    <button
                                      type="button"
                                      className="btn btn-secondary"
                                      data-bs-dismiss="modal"
                                      id="closeModalBtn"
                                    >
                                      close
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};
export default BillingPage;
